import {
  booleanState,
  businessMoney,
  captured,
  count,
  dates,
  dollars,
  emails,
  eventWindow,
  noValues,
  optionalSelection,
  pastWindows,
  selection,
  yesNo,
  type SourceCategory,
} from "./crmFilterTaxonomy.vocabulary";
/** Exact v1.5 source rows, categories 16–23; never infer data availability here. */
export const CRM_TAXONOMY_PORTAL_CATEGORIES = [
  {
    id: "timeline",
    number: 16,
    label: "Timeline",
    fields: [
      [
        "last_activity_date",
        "Last activity date",
        ["Is within", "Before", "After", "Between", "More than", "Never"],
        pastWindows,
      ],
      ["created_date", "Created date", dates, pastWindows],
      ["became_user_date", "Became user date", dates, pastWindows],
    ],
  },
  {
    id: "tags",
    number: 17,
    label: "Tags",
    fields: [
      [
        "has_tag",
        "Has tag",
        selection,
        ["Live tag list from Audiences › Tags"],
      ],
    ],
  },
  {
    id: "email_comms",
    number: 18,
    label: "Email & comms",
    fields: [
      [
        "email_sent",
        "Email sent",
        ["Has happened", "Has not happened", "Is within", "Before", "After"],
        emails,
      ],
      ["email_opened", "Email opened", eventWindow, emails],
      ["email_clicked", "Email clicked", eventWindow, emails],
      ["email_bounced", "Email bounced", eventWindow, emails],
      ["email_failed", "Email failed", eventWindow, emails],
      [
        "email_consent",
        "Email consent",
        ["Is subscribed to", "Is not subscribed to"],
        [
          "Account & billing",
          "Reports & financial summaries",
          "Alerts & reminders",
          "Product updates",
          "Learning & education",
          "Marketing & offers",
          "Research & feedback",
        ],
      ],
      ["email_suppressed", "Email suppressed", booleanState, yesNo],
      [
        "suppression_reason",
        "Suppression reason",
        selection,
        [
          "Bounce",
          "Complaint",
          "Unsubscribed",
          "Invalid email",
          "Do not contact",
          "Provider block",
        ],
      ],
      [
        "email_send_type",
        "Email send type",
        selection,
        [
          "Transactional",
          "Billing",
          "Reports",
          "Customer success",
          "Product education",
          "Alerts",
          "Support",
          "Marketing",
          "Research",
        ],
      ],
      [
        "email_source",
        "Email source",
        selection,
        ["Broadcast", "Automation", "Manual", "System", "Test"],
      ],
    ],
  },
  {
    id: "live_alerts",
    number: 19,
    label: "Live alerts",
    fields: [
      ["has_open_alert", "Has open alert", booleanState, yesNo],
      [
        "alert_type",
        "Alert type",
        selection,
        ["No login", "Billing", "Support", "Bug", "Onboarding", "Partner"],
      ],
      [
        "alert_priority",
        "Alert priority",
        selection,
        ["Critical", "High", "Medium", "Low"],
      ],
      [
        "alert_status",
        "Alert status",
        selection,
        [
          "Open",
          "In progress",
          "Demo call booked",
          "Waiting on customer",
          "Waiting on internal team",
          "Escalated to bugs/fixes",
          "Escalated to improvements/features",
          "Stuck",
          "Resolved",
          "Archived",
        ],
      ],
      ["alert_overdue", "Alert overdue", booleanState, yesNo],
    ],
  },
  {
    id: "automation_workflows",
    number: 20,
    label: "Automation workflows",
    fields: [
      [
        "is_in_workflow",
        "Is in workflow",
        selection,
        ["Live automation list from Automation Centre"],
      ],
      [
        "workflow_status",
        "Workflow status",
        selection,
        ["Active", "Completed", "Exited", "Paused", "Failed"],
      ],
      [
        "workflow_step",
        "Workflow step",
        [
          "Is current",
          "Has completed",
          "Has not completed",
          "Has skipped",
          "Has failed",
        ],
        ["Step name from the named workflow"],
      ],
      [
        "workflow_exit_reason",
        "Workflow exit reason",
        selection,
        [
          "Completed",
          "Churned",
          "Manually exited",
          "No longer qualifies",
          "Replaced by workflow",
        ],
      ],
    ],
  },
  {
    id: "support_feedback",
    number: 21,
    label: "Support & feedback",
    fields: [
      ["has_open_support_item", "Has open support item", booleanState, yesNo],
      [
        "support_status",
        "Support status",
        selection,
        ["Open", "In progress", "Waiting on customer", "Resolved"],
      ],
      [
        "issue_report_status",
        "Issue report status",
        selection,
        [
          "New",
          "Triaging – dev",
          "More information needed",
          "Waiting on customer",
          "Confirmed – queued",
          "In progress",
          "Blocked with developer",
          "Ready for review – in staging",
          "Closed – live",
          "Won't fix",
          "Duplicate",
        ],
      ],
      [
        "feature_request_status",
        "Feature request status",
        selection,
        ["Values to be set when the feature request process is scoped"],
      ],
      ["feedback_submitted", "Feedback submitted", eventWindow, noValues],
    ],
  },
  {
    id: "about_the_business",
    number: 22,
    label: "About the business",
    fields: [
      ["industry", "Industry", optionalSelection, ["Live industry list"]],
      ["country", "Country", selection, ["Live country list"]],
      ["timezone", "Timezone", selection, ["Live timezone list"]],
      ["annual_revenue", "Annual revenue", businessMoney, dollars],
      ["annual_expenses", "Annual expenses", businessMoney, dollars],
      ["employees", "Employees", count, noValues],
      ["transactions_per_month", "Transactions per month", count, noValues],
      ["bank_accounts_connected", "Bank accounts connected", count, noValues],
    ],
  },
  {
    id: "source_attribution",
    number: 23,
    label: "Source & attribution",
    fields: [
      [
        "signup_source",
        "Signup source",
        optionalSelection,
        ["Live source list"],
      ],
      ["utm_source", "UTM source", optionalSelection, captured],
      ["utm_campaign", "UTM campaign", optionalSelection, captured],
      ["utm_medium", "UTM medium", optionalSelection, captured],
    ],
  },
] as const satisfies readonly SourceCategory[];
